import { useCallback, useEffect, useRef } from "react";
import { meApiService } from "../cores/me/services/api";
import { resolveActivityModule, shouldTrackActivityPath } from "../cores/router/resolveActivityModule";

const HEARTBEAT_INTERVAL_MS = 3 * 60 * 1000;
const ROUTE_DEBOUNCE_MS = 30 * 1000;

/**
 * Sends lightweight activity heartbeats while the user has an active session.
 * Failures are ignored so the app never depends on this side channel.
 */
export function useUserActivityHeartbeat(pathname: string, enabled: boolean) {
  const lastSentAtRef = useRef(0);
  const pathnameRef = useRef(pathname);

  const sendActivity = useCallback(
    (path: string, force = false) => {
      if (!enabled || !shouldTrackActivityPath(path)) return;

      const module = resolveActivityModule(path);
      if (!module) return;

      const now = Date.now();
      if (!force && now - lastSentAtRef.current < ROUTE_DEBOUNCE_MS) return;

      lastSentAtRef.current = now;
      meApiService.postMeActivity({ module, path }).catch(() => undefined);
    },
    [enabled]
  );

  useEffect(() => {
    pathnameRef.current = pathname;
    sendActivity(pathname);
  }, [pathname, sendActivity]);

  useEffect(() => {
    if (!enabled) return undefined;

    sendActivity(pathnameRef.current, true);
    const timer = window.setInterval(
      () => sendActivity(pathnameRef.current, true),
      HEARTBEAT_INTERVAL_MS
    );

    return () => window.clearInterval(timer);
  }, [enabled, sendActivity]);
}
